import { useMutation } from '@tanstack/react-query'
import { Check, RotateCw, Trash2 } from 'lucide-react'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary } from '../index.js'
import { Modal } from '../../core/index.js'
import { Button } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

/**
 * The trash button on the row of a registration that never activated - an
 * onboarding that stopped before the key was proved, so there is a
 * registration and nothing under it. It only opens the dialog; nothing is
 * deleted from here. (A client with a clientId has its own delete, demo
 * clients only.)
 */
export function DeleteRegistrationButton({ client, onClick }: { client: AdminClientSummary; onClick: () => void }) {
  return (
    <button
      type="button"
      title={`Delete unactivated registration ${client.organizationName}`}
      aria-label={`Delete unactivated registration ${client.organizationName}`}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="inline-flex items-center rounded border border-coral/50 px-1.5 py-1 text-coral hover:bg-coral-lt"
    >
      <Trash2 aria-hidden="true" strokeWidth={2} className="h-3.5 w-3.5" />
    </button>
  )
}

interface DeleteRegistrationModalProps {
  api: AdminApi
  client: AdminClientSummary
  /** Told when the registration is gone, so the list behind refreshes. */
  onDeleted: (client: AdminClientSummary) => void
  onClose: () => void
}

/**
 * Removing a registration that never became a client: one call,
 * `DELETE /admin/m2m/registrations/{registrationId}`, which the API refuses
 * for anything that has a clientId. Frees the organization's key id for a
 * fresh onboarding. Irreversible, so nothing happens until Delete
 * registration is pressed.
 */
export function DeleteRegistrationModal({ api, client, onDeleted, onClose }: DeleteRegistrationModalProps) {
  const remove = useMutation({ mutationFn: () => api.deleteRegistration(client.registrationId), onSuccess: () => onDeleted(client) })

  return (
    <Modal
      eyebrow="Unactivated registration"
      title={remove.isSuccess ? `Deleted ${client.organizationName}` : `Delete ${client.organizationName}`}
      subtitle={remove.isSuccess ? 'The registration is gone and its key id is free again.' : 'This onboarding stopped before the client was activated, so there is nothing under it but the registration itself.'}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-4">
          {remove.isSuccess ? (
            <Button icon={Check} onClick={onClose} autoFocus>
              Close
            </Button>
          ) : (
            <>
              <Button icon={remove.isError ? RotateCw : Trash2} onClick={() => remove.mutate()} disabled={remove.isPending}>
                {remove.isPending ? 'Deleting…' : remove.isError ? 'Try again' : 'Delete registration'}
              </Button>
              <button type="button" onClick={onClose} disabled={remove.isPending} className="text-xs font-bold text-ink-mid hover:text-ink disabled:opacity-40">
                Cancel
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="px-6 py-5 text-sm text-ink space-y-2">
        <p>
          Removes the registration for <span className="font-bold">{client.organizationName}</span> ({client.contactEmail}) and the key id it claimed. It cannot be undone, and there is no
          client, facility or worker to lose: none was created.
        </p>
        {remove.isError && (
          <div className="border border-coral bg-coral-lt p-4 text-sm text-ink">
            <div className="font-bold text-coral mb-1">Not deleted</div>
            <p>{remove.error.message}</p>
          </div>
        )}
      </div>
    </Modal>
  )
}
